import { ChevronsIcon } from "@minket/brand/icons/chevrons";
import { ThemeIcon } from "@minket/brand/icons/theme";
import { useTheme } from "@minket/ui/hooks/theme";
import { focusRingSidebar } from "@minket/ui/lib/styles";
import { cn } from "@minket/ui/lib/utils";
import type { ComponentProps } from "react";
import { FitText } from "@/components/page/fit-text";
import type { SessionUser } from "@/lib/session";
import { useWorkspace } from "@/lib/workspace";
import { scopeLines } from "./project-line";
import { themeLabels } from "./theme-labels";

/**
 * The rail's three menu buttons: the scope (organization and project), the theme and the
 * account. The same button is the rail's control before its menu has loaded (deferred.tsx) and
 * the menu's trigger once it has (Radix passes its props and ref through `asChild`), so nothing
 * moves or changes when the menu takes over.
 */
type ButtonProps = ComponentProps<"button">;

const hover = "transition-colors duration-150 ease-out-quiet hover:bg-sidebar-accent";

/**
 * Screen reader words between two names end in a no-break space: a visually hidden span's
 * trailing space collapses, and two stacked lines are not always parted in a computed name.
 */
const NBSP = "\u00a0";
const chevrons = "shrink-0 text-muted-foreground";

/**
 * The scope, stacked (the owner's rule): the organization on the first line, small and muted,
 * the project on the second in the strong weight, each across the switcher's full width on one
 * line, the chevrons centred on both. The words "organization" and "project" are said, not
 * shown: screen reader text around the names makes the button's name "Organization Personal,
 * project Lynxtrades", so the visible names stay part of it. Names are bounded (32 characters)
 * and narrow to fit (FitText); none wraps.
 */
export function SwitcherButton({ className, ...props }: ButtonProps) {
  const lines = scopeLines(useWorkspace());
  return (
    <button
      type="button"
      aria-haspopup="menu"
      {...props}
      className={cn(
        focusRingSidebar,
        hover,
        "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 rounded-md border border-sidebar-border px-3 py-2 text-left short:py-1.5",
        className,
      )}
    >
      <span className="grid min-w-0">
        <FitText className="text-[12.5px] leading-[18px] text-muted-foreground">
          <span className="sr-only">Organization{NBSP}</span>
          {lines.org}
          <span className="sr-only">
            {lines.project.known ? ", project" : ". Project:"}
            {NBSP}
          </span>
        </FitText>
        <FitText
          className={cn(
            "text-[14.5px] leading-5",
            lines.project.known ? "font-semibold text-sidebar-foreground" : "text-muted-foreground",
          )}
        >
          {lines.project.text}
        </FitText>
      </span>
      <ChevronsIcon size={18} className={chevrons} />
    </button>
  );
}

export function ThemeButton({ className, ...props }: ButtonProps) {
  const { preference } = useTheme();
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-label={`Theme: ${themeLabels[preference]}`}
      {...props}
      className={cn(
        focusRingSidebar,
        hover,
        "grid size-8 shrink-0 place-items-center rounded-md text-sidebar-foreground hover:text-sidebar-accent-foreground",
        className,
      )}
    >
      <ThemeIcon size={20} className="text-muted-foreground" />
    </button>
  );
}

/** "Sam Rivera" → "SR"; a person known only by address gets its first letter. */
export function initials(user: SessionUser): string {
  const words = (user.name ?? "").trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? user.email.charAt(0);
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

/**
 * The signed-in person: an initials disc, the name (or the address) and, under a name, the
 * address, each on one line (narrowed to fit, FitText), and the chevrons that say it opens the
 * account menu. Plain words, no rule under them: it names who is signed in rather than reading as
 * a link (the owner, 2026-10-29). On a window under 560 px tall the rail folds its foot onto one
 * line and the button keeps only the disc, the name and address staying in it as screen reader
 * text, and the menu repeats them.
 */
export function AccountButton({ className, ...props }: ButtonProps) {
  const { user } = useWorkspace();
  return (
    <button
      type="button"
      aria-haspopup="menu"
      {...props}
      className={cn(
        focusRingSidebar,
        hover,
        "grid w-full min-w-0 grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-md px-2 py-1.5 text-left",
        "short:w-auto short:grid-cols-[28px] short:px-1.5 short:py-1 short:[&>*:not(:first-child)]:sr-only",
        className,
      )}
    >
      <span
        aria-hidden
        className="grid size-7 place-items-center rounded-full bg-primary/12 text-[11.5px] font-semibold text-primary"
      >
        {initials(user)}
      </span>
      <span className="grid min-w-0">
        {user.name ? (
          <FitText className="text-[14px] font-medium text-sidebar-foreground">
            {user.name}
            <span className="sr-only">,{NBSP}</span>
          </FitText>
        ) : null}
        <FitText
          className={
            user.name
              ? "type-data text-[12px] text-muted-foreground"
              : "text-[13.5px] font-medium text-sidebar-foreground"
          }
        >
          {user.email}
        </FitText>
      </span>
      <ChevronsIcon size={18} className={chevrons} />
    </button>
  );
}
